import { useState, useRef, useEffect } from 'react';
import { navigateTo } from '@/Utils/routing';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TranslateIcon from '@mui/icons-material/Translate';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

import { usePosts, useFeeds } from '@/Recoil/Social';
import { getHashtagRecommendations } from '@/Data/Social/Hashtag';
import { HashtagRecommendationType } from '@/Data/Social/types';
import Dialog from '@/Components/Dialog';
import { useImageUploader } from '@/hooks/useImageUploader';

import './newPost.scss';
import { useSession } from '@/Recoil/Session/Session';

const CreatePost = () => {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const postid = urlParams.get('postid');
    const hashtagsParam = urlParams.get('hashtags');
    
    const { posts, createPost, updatePost, loading } = usePosts();
    const { addPostToHomeFeed } = useFeeds();
    const { session } = useSession();
    
    const isEditMode = !!postid;
    const editingPost = isEditMode && postid ? posts[postid] : null;
    
    const [content, setContent] = useState('');
    const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);
    const [recommendedHashtags, setRecommendedHashtags] = useState<HashtagRecommendationType[]>([]);
    const [loadingRecommendations, setLoadingRecommendations] = useState(false);
    const { images: selectedImages, addImages, removeImage, setImages } = useImageUploader();
    const [isWriting, setIsWriting] = useState(false);

    const [askToLeave, setAskToLeave] = useState(false);
    const [visibility, setVisibility] = useState<'public' | 'private'>('public');
    const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);
    
    const textInputRef = useRef<HTMLTextAreaElement>(null);
    const activeRef = useRef(false);
    const topicRecTimer = useRef<NodeJS.Timeout | null>(null);
    const contentRef = useRef(content);
    const latelyKnownContent = useRef<string>(content);
    useEffect(() => { contentRef.current = content; }, [content]);

    // Pre-populate hashtags from MrMate navigation
    useEffect(() => {
        if (hashtagsParam && typeof hashtagsParam === 'string' && !isEditMode) {
            const decodedHashtags = decodeURIComponent(hashtagsParam);
            const hashtagArray = decodedHashtags.split(',');
            const cleanHashtags = hashtagArray.map(tag => 
                tag.startsWith('#') ? tag.substring(1) : tag
            );
            setSelectedHashtags(cleanHashtags);
        }
    }, [hashtagsParam, isEditMode]);
    
    // Load post data for editing
    useEffect(() => {
        if (isEditMode && editingPost) {
            // Check if user owns this post
            if (editingPost.addressid !== session?.addressid) {
                alert('Error: You can only edit your own posts');
                navigateTo('/social');
                return;
            }
            
            setContent(editingPost.content || '');
            setSelectedHashtags(editingPost.hashtags || []);
            setIsWriting(true);
            setVisibility(editingPost.visibility as 'public'|'private' || 'public');
            
            // Handle existing images if any
            if (editingPost.media && editingPost.media.length > 0) {
                const existingImages = editingPost.media.map((s3Url, index) => ({
                    localIdentifier: `existing-${index}`,
                    uri: s3Url,
                    s3Url,
                    uploadState: 'uploaded' as const
                }));
                setImages(existingImages);
            }
        }
    }, [isEditMode, editingPost, session?.addressid, setImages]);
    
    useEffect(() => {
        activeRef.current = true; // Mark as active for the current render
        
        topicRecTimer.current = setInterval(async () => {
            if (!activeRef.current) {
                clearInterval(topicRecTimer.current ?? undefined);
                topicRecTimer.current = null;
                return; // Only proceed if still active
            }
            if (contentRef.current.trim() === '') {
                setRecommendedHashtags([]);
                return;
            }
            if (contentRef.current.trim() === latelyKnownContent.current.trim()) {
                return;
            }

            setLoadingRecommendations(true);
            try {
                latelyKnownContent.current = contentRef.current;
                const recommendations = await getHashtagRecommendations(contentRef.current, 8);
                if (activeRef.current && recommendations) {
                    setRecommendedHashtags(recommendations);
                }
            } catch (error) {
                console.error('Failed to get hashtag recommendations:', error);
            } finally {
                if (!activeRef.current) {
                    clearInterval(topicRecTimer.current ?? undefined);
                    topicRecTimer.current = null;
                    return; // Only proceed if still active
                }
                setLoadingRecommendations(false);
            }

        }, 2000);
        return () => {
            activeRef.current = false; // Mark as inactive on cleanup
        };
    }, []);
    
    
    const handleToggleHashtag = (tagName: string) => {
        setSelectedHashtags(prev => {
            if (prev.includes(tagName)) {
                return prev.filter(tag => tag !== tagName);
            } else {
                return [...prev, tagName];
            }
        });
    };
    
    const handleRemoveImage = (localIdentifier: string) => {
        removeImage(localIdentifier);
    };
    
    // Update isWriting when images change
    useEffect(() => {
        setIsWriting(selectedImages.length > 0);
    }, [selectedImages]);
    
    const handlePost = async () => {
        if (!content.trim()) {
            alert('Error: Please write something to post');
            return;
        }
        
        try {
            
            // Get S3 URLs from uploaded images
            const imageKeys: string[] = selectedImages
                .filter(image => image.uploadState === 'uploaded' && image.s3Url)
                .map(image => image.s3Url!);
            
            if (isEditMode && postid) {
                // Update existing post
                const result = await updatePost({
                    postid,
                    content: content.trim(),
                    media: imageKeys.length > 0 ? imageKeys : undefined,
                    hashtags: selectedHashtags.length > 0 ? selectedHashtags : undefined
                });
                
                if (result) {
                    navigateTo('/social');
                } else {
                    alert('Error: Failed to update post');
                }
            } else {
                // Create new post
                const result = await createPost({
                    content: content.trim(),
                    visibility: visibility,
                    media: imageKeys.length > 0 ? imageKeys : undefined,
                    hashtags: selectedHashtags.length > 0 ? selectedHashtags : undefined
                });
                
                if (result) {
                    // Add the new post to home feed for immediate visibility
                    addPostToHomeFeed(result.postid);
                    navigateTo('/social');
                } else {
                    alert('Error: Failed to create post');
                }
            }
        } catch (error) {
            console.error('Post operation error:', error);
            alert('Error: ' + (isEditMode ? 'Failed to update post' : 'Failed to create post'));
        } finally {
            // Upload completed
        }
    };
    
    const canPost = content.trim().length > 0 && !loading;
    
    return (
        <div className="NewPost">
            <div className="SafeAreaView Container">
            {/* Header */}
            <div className="View Header">
                <button 
                    className="Pressable CancelButton"
                    onClick={() => {
                        if (selectedImages.length > 0 || content.trim() !== '') {
                            setAskToLeave(true);
                            return;
                        }
                        navigateTo('/social');
                    }}
                >
                    <span className="Text CancelButtonText">cancel</span>
                </button>
                <div className="View HeaderRight">
                    <button 
                        className="Pressable VisibilityButton"
                        onClick={() => setShowVisibilityDialog(true)}
                    >
                        <span className="Text VisibilityButtonText">
                            {visibility === 'private' ? 'Private' : 'Public'}
                        </span>
                        <ArrowDropDownIcon />
                    </button>
                    <button 
                        className={`Pressable PostButton ${canPost ? 'PostButtonActive' : 'PostButtonDisabled'}`}
                        onClick={handlePost}
                        disabled={!canPost}
                    >
                        <span className={`Text PostButtonText ${canPost ? 'PostButtonTextActive' : 'PostButtonTextDisabled'}`}>
                            {loading ? (isEditMode ? 'Updating...' : 'Posting...') : (isEditMode ? 'Update' : 'Post')}
                        </span>
                    </button>
                </div>
            </div>
            
            <div className="KeyboardAvoidingView">
                <div className="ScrollView">
                    {/* Text Input */}
                    <div className={`View ContentSection ${isWriting ? 'ContentSectionWriting' : ''}`}>
                        <textarea
                            ref={textInputRef}
                            className={`TextInput ${isWriting ? 'TextInputWriting' : ''}`}
                            placeholder={isWriting ? '' : `${session?.name || 'user'}, write a new post!`}
                            value={content}
                            onChange={(e) => {
                                setContent(e.target.value);
                                setIsWriting(e.target.value.length > 0 || selectedImages.length > 0);
                            }}
                            onFocus={() => setIsWriting(true)}
                        />
                    </div>
                    
                    {/* Combined Hashtags - Selected + Candidates */}
                    <div className="View HashtagSection">
                        <div className="View HashtagHeader">
                            <span className="Text HashtagTitle">
                                {(recommendedHashtags.length === 0 && selectedHashtags.length === 0) 
                                    ? 'Hashtags will be recommended based on content.' 
                                    : 'Recommended Hashtags'}
                            </span>
                            {loadingRecommendations && (
                                <span className="Text LoadingText">Loading...</span>
                            )}
                        </div>
                        <div className="View HashtagButtons">
                            {/* Selected hashtags first (with delete buttons) */}
                            {selectedHashtags.map((tagName) => (
                                <button
                                    key={`selected-${tagName}`}
                                    className="Pressable HashtagButton HashtagButtonSelected"
                                    onClick={() => handleToggleHashtag(tagName)}
                                >
                                    <span className="Text HashtagButtonText HashtagButtonTextSelected">
                                        #{tagName}
                                    </span>
                                    <CloseIcon />
                                </button>
                            ))}
                            {/* Candidate hashtags (clickable to select) */}
                            {recommendedHashtags
                                .filter(hashtag => !selectedHashtags.includes(hashtag.tag_name))
                                .map((hashtag) => (
                                <button
                                    key={`candidate-${hashtag.tag_name}`}
                                    className="Pressable HashtagButton"
                                    onClick={() => handleToggleHashtag(hashtag.tag_name)}
                                >
                                    <span className="Text HashtagButtonText">
                                        #{hashtag.tag_name}
                                    </span>
                                    {hashtag.post_count > 10 && (
                                        <div className="View HotBadge">
                                            <span className="Text NewBadgeText">{hashtag.post_count}</span>
                                        </div>
                                    )}
                                    {hashtag.is_new && (
                                        <div className="View NewBadge">
                                            <span className="Text NewBadgeText">NEW</span>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    {/* Translation Notice */}
                    <div className="View TranslationNotice">
                        <TranslateIcon />
                        <span className="Text TranslationNoticeText">
                            Translation will be generated automatically after you save the post.
                        </span>
                    </div>
                    
                    {/* Selected Images */}
                    {selectedImages.length > 0 && (
                        <div className="View ImagesSection">
                            <div className="ScrollView ImageScrollView">
                                <div className="View ImagesContainer">
                                    {selectedImages.map((imageItem) => (
                                        <div key={imageItem.localIdentifier} className="View ImageWrapper">
                                            <div className="View ImagePlaceholder">
                                                <img src={imageItem.uri} className="FastImage SelectedImage" alt="" />
                                            </div>
                                            <button 
                                                className="Pressable RemoveImageButton"
                                                onClick={() => handleRemoveImage(imageItem.localIdentifier)}
                                            >
                                                <CloseIcon />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Add Image Button */}
                    <div className="View NewImageButton">
                        <label className="Pressable AddImageButton">
                            <input
                                type="file"
                                multiple
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    if (files.length + selectedImages.length > 10) {
                                        alert('Maximum Images: You can only add up to 10 images');
                                        return;
                                    }
                                    addImages(files);
                                }}
                            />
                            <AddPhotoAlternateIcon />
                            <span className="Text AddImageText">Add Images</span>
                        </label>
                    </div>
                </div>
            </div>
            
            <Dialog
                visible={askToLeave}
                onDismiss={() => setAskToLeave(false)}
            >
                <div className="DialogContent">
                    <h2 className="Text DialogHeadline">Are you sure to leave?</h2>
                    <p className="Text DialogSubline">If you leave, editing contents will be erased.</p>
                    <button 
                        className="Pressable DialogButton DialogPrimaryButton" 
                        onClick={() => {
                            navigateTo('/social');
                            setAskToLeave(false);
                        }}
                    >
                        <span className="Text DialogPrimaryButtonText">Leave</span>
                    </button>
                    <button 
                        className="Pressable DialogButton" 
                        onClick={() => setAskToLeave(false)}
                    >
                        <span className="Text DialogButtonText">Cancel</span>
                    </button>
                </div>
            </Dialog>
            
            <Dialog
                visible={showVisibilityDialog}
                onDismiss={() => setShowVisibilityDialog(false)}
            >
                <div className="DialogContent">
                    <h2 className="Text DialogHeadline">Post Visibility</h2>
                    <p className="Text DialogSubline">Choose who can view this post</p>
                    <button 
                        className={`Pressable DialogButton ${visibility === 'public' ? 'DialogPrimaryButton' : 'DialogSecondaryButton'}`}
                        onClick={() => { 
                            setVisibility('public');
                            setShowVisibilityDialog(false);
                        }}
                    >
                        <span className={`Text ${visibility === 'public' ? 'DialogPrimaryButtonText' : 'DialogSecondaryButtonText'}`}>
                            Public
                        </span>
                    </button>
                    <button 
                        className={`Pressable DialogButton ${visibility === 'private' ? 'DialogPrimaryButton' : 'DialogSecondaryButton'}`}
                        onClick={() => { 
                            setVisibility('private');
                            setShowVisibilityDialog(false);
                        }}
                    >
                        <span className={`Text ${visibility === 'private' ? 'DialogPrimaryButtonText' : 'DialogSecondaryButtonText'}`}>
                            Private
                        </span>
                    </button>
                </div>
            </Dialog>
        </div>
        </div>
    );
};

export default CreatePost;